import React from 'react'

import { getDisplayUserName } from 'utils/strings'
import { isAmountExisting } from 'utils/activity.util'

// styles
import styles from './index.module.scss'
import { countDecimals, getBuyerName, getLocalDate, getOwnerName } from 'utils/helpers'
import ActivityTableItem from './ActivityTableItem'
import Link from 'next/link'
import { ActivityAPIData } from 'types/nft'

type ActivityTableTypes = {
  data: ActivityAPIData[]
  type: any
}

const ActivityTable = ({ data, type }: ActivityTableTypes) => {
  const getUserName = item => {
    let user = ''
    switch (item.action_type) {
      case 'AuctionStarted':
      case 'AuctionCancelled':
      case 'ListingAdded':
      case 'ListingCancelled':
      case 'MasterEditionListingCreated':
      case 'MasterEditionListingUpdated':
      case 'MasterEditionPrintCreated':
      case 'MasterEditionListingCancelled':
      case 'ListingUpdated':
        user = getOwnerName(item)
        break
      case 'OfferMade':
      case 'OfferCancelled':
      case 'QuickBuy':
      case 'AuctionWon':
      case 'BidPlaced':
        user = getBuyerName(item)
        break
    }
    return user
  }

  const getActionType = item => {
    let type = ''
    switch (item.action_type) {
      case 'AuctionStarted':
        type = 'started an auction for'
        break
      case 'AuctionCancelled':
        type = 'canceled the auction for'
        break
      case 'ListingAdded':
        type = 'listed'
        break
      case 'ListingCancelled':
        type = 'delisted'
        break
      case 'OfferMade':
        type = 'placed a'
        break
      case 'OfferCancelled':
        type = 'withdrew the'
        break
      case 'QuickBuy':
        type = 'purchased'
        break
      case 'AuctionWon':
        type = 'won the auction for'
        break
      case 'BidPlaced':
        type = 'bid'
        break
      case 'MasterEditionListingCreated':
        type = 'listed'
        break
      case 'MasterEditionListingUpdated':
        type = 'updated the mint price for'
        break
      case 'MasterEditionPrintCreated':
        type = 'collected edition'
        break
      case 'MasterEditionListingCancelled':
        type = 'delisted'
        break
      case 'ListingUpdated':
        type = 'updated listing'
        break
    }
    return type
  }

  const getActivityType = item => {
    let type = ''
    switch (item.action_type) {
      case 'AuctionStarted':
      case 'ListingAdded':
        type = 'List'
        break
      case 'AuctionCancelled':
      case 'ListingCancelled':
        type = 'Delist'
        break
      case 'OfferMade':
      case 'OfferCancelled':
        type = 'Offer'
        break
      case 'QuickBuy':
      case 'AuctionWon':
        type = 'Buy'
        break
      case 'BidPlaced':
        type = 'Bid'
        break
      case 'MasterEditionListingCreated':
        type = 'List'
        break
      case 'MasterEditionListingUpdated':
        type = 'List'
        break
      case 'MasterEditionPrintCreated':
        type = 'Buy'
        break
      case 'MasterEditionListingCancelled':
        type = 'Delist'
        break
      case 'ListingUpdated':
        type = 'List'
        break
    }
    return type
  }

  const getPrintAssetName = item => {
    let edition = item.asset_name.slice(item.asset_name.indexOf('#'))
    edition = edition.slice(0, edition.indexOf(', (#'))
    return edition + ' of ' + item.asset_name.slice(0, item.asset_name.indexOf('#'))
  }

  return (
    <div className="w-full">
      {data.map((item, index) => (
        <div key={index} className={styles.row}>
          <div className="w-full flex justify-between items-center">
            <div className="flex items-center gap-[15px]">
              <div className={styles.activityType}>{getActivityType(item)}</div>
              <Link href={`/nft/${item.mint_address}`} passHref>
                <a>
                  <div className="w-[40px] h-[40px]">
                    <ActivityTableItem url={item.asset_url} />
                  </div>
                </a>
              </Link>
              <div className="flex items-center justify-between">
                <div className="flex">
                  <Link href={`/${getUserName(item)}`} passHref>
                    <a>
                      <div className={styles.userName}>{getDisplayUserName(getUserName(item))}</div>
                    </a>
                  </Link>
                  <div className={styles.text}>&nbsp;{getActionType(item)}</div>
                  {item.action_type === 'OfferMade' ||
                  item.action_type === 'OfferCancelled' ||
                  item.action_type === 'BidPlaced' ? (
                    <div className="flex items-center">
                      {isAmountExisting(item.action_type) && (
                        <>
                          <div className={styles.text}>
                            &nbsp;
                            {countDecimals(parseFloat(item.amount)) > 2
                              ? parseFloat(item.amount).toFixed(2)
                              : item.amount}
                            &nbsp;SOL
                          </div>
                        </>
                      )}
                      <div className={styles.text}>
                        &nbsp;
                        {item.action_type === 'OfferMade'
                          ? 'offer on'
                          : item.action_type === 'OfferCancelled'
                          ? 'offer for'
                          : 'on'}
                        &nbsp;
                      </div>
                      <div className="block">
                        <Link href={`/nft/${item.mint_address}`} passHref>
                          <a>
                            <div className={styles.nftName}>{item.asset_name}</div>
                          </a>
                        </Link>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center">
                      <div className="block">
                        <Link href={`/nft/${item.mint_address}`} passHref>
                          <a>
                            <div className={styles.nftName}>
                              &nbsp;
                              {item.action_type === 'MasterEditionPrintCreated'
                                ? getPrintAssetName(item)
                                : item.asset_name}
                            </div>
                          </a>
                        </Link>
                      </div>
                      {isAmountExisting(item.action_type) && (
                        <>
                          <div className={styles.text}>
                            &nbsp;for&nbsp;
                            {countDecimals(parseFloat(item.amount)) > 2
                              ? parseFloat(item.amount).toFixed(2)
                              : item.amount}
                            &nbsp;SOL
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className={styles.date}>{getLocalDate(item.created_at)}</div>
            {/* <div className={styles.view}>
              <div className={styles.border} />
              <Link href={`/nft/${item.mint_address}`} passHref>
                <a>View</a>
              </Link>
            </div> */}
          </div>
          <div className={styles.border} />
        </div>
      ))}
    </div>
  )
}

export default ActivityTable
